import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, Users, FileText, LayoutDashboard, 
  CheckCircle, ArrowRight, XCircle, Lightbulb,
  PieChart, FolderKanban, Receipt
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const problems = [
  "Logiciels compliques avec trop de fonctionnalites",
  "Interfaces peu intuitives",
  "Outils concus par des developpeurs, pas des artisans",
  "Perte de temps a comprendre le logiciel",
];

const solutions = [
  { icon: Users, text: "Gerer tes prospects et clients" },
  { icon: FolderKanban, text: "Suivre tes chantiers en temps reel" },
  { icon: FileText, text: "Creer tes devis en quelques clics" },
  { icon: Receipt, text: "Gerer tes factures facilement" },
  { icon: PieChart, text: "Analyser ton activité" },
];

const benefits = [
  "Interface simple et intuitive",
  "Concu pour le terrain",
  "Prise en main immediate",
  "Support reactif",
];

export default function CRMBatimentPage() {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        page="crm"
        title="CRM Bâtiment | Gestion Clients, Prospects & Chantiers - BatiAssist"
        description="Le CRM conçu pour les artisans du bâtiment : gestion prospects, suivi clients, pipeline commercial, historique chantiers. Interface simple et intuitive."
        keywords="CRM bâtiment, gestion client artisan, logiciel prospect BTP, suivi chantier, pipeline commercial construction"
        path="/fonctionnalites/crm-batiment"
      />
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-ba-primary/10 text-ba-primary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Building2 className="w-4 h-4" />
              CRM Batiment
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Le CRM cree par un{' '}
              <span className="text-ba-secondary">entrepreneur du batiment</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-8 leading-relaxed">
              BatiAssist a ete concu par un entrepreneur ayant construit plus de <strong>1500 piscines dans 7 pays</strong>. 
              Apres avoir utilise de nombreux logiciels complexes et couteux, il a decide de creer le CRM qu'il aurait aime avoir.
            </p>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10">
              Un outil <strong>simple</strong>, <strong>rapide</strong> et <strong>adapte a la realite du terrain</strong>.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all">
                Decouvrir le CRM BatiAssist
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <div>
              <span className="inline-block px-3 py-1 bg-red-100 text-red-600 text-xs font-semibold uppercase rounded-full mb-4">
                Le probleme
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Des logiciels concus par des developpeurs, pas des artisans
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Beaucoup de CRM sont crees par des equipes techniques qui ne connaissent pas la realite du terrain. 
                Le resultat ? Des outils compliques et inadaptes.
              </p>
            </div>
            <div className="space-y-4">
              {problems.map((problem, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-4 bg-red-50 rounded-xl border border-red-100"
                >
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700">{problem}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-ba-success/10 text-ba-success text-xs font-semibold uppercase rounded-full mb-4">
              <Lightbulb className="w-4 h-4" />
              La solution
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              BatiAssist : tout ce dont tu as besoin
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un CRM simple et efficace qui te permet de gérer ton activité sans prise de tete.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => {
              const Icon = solution.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-6 rounded-2xl border border-slate-200 hover:border-ba-primary/30 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-ba-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-ba-primary" />
                  </div>
                  <p className="text-slate-700 font-medium">{solution.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Une interface claire et intuitive
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Visualise ton activite en un coup d'oeil avec notre tableau de bord.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-br from-ba-primary to-blue-700 rounded-3xl p-8 md:p-12"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-ba-primary mb-2">24</p>
                  <p className="text-slate-600">Prospects actifs</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-ba-secondary mb-2">12</p>
                  <p className="text-slate-600">Devis en attente</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-6 text-center">
                  <p className="text-4xl font-bold text-ba-success mb-2">8</p>
                  <p className="text-slate-600">Chantiers en cours</p>
                </div>
              </div>
              <div className="space-y-3">
                {['Famille Dupont - Renovation salle de bain', 'Entreprise Martin - Installation piscine', 'Villa Garcia - Terrasse exterieure'].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                    <span className="font-medium text-slate-700">{item}</span>
                    <span className="px-3 py-1 bg-ba-success/10 text-ba-success text-sm rounded-full">En cours</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Les avantages BatiAssist
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <CheckCircle className="w-6 h-6 text-ba-success flex-shrink-0" />
                    <span className="text-lg text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-ba-primary rounded-3xl p-8 text-white"
            >
              <Building2 className="w-12 h-12 mb-4 opacity-80" />
              <h3 className="text-2xl font-bold mb-4">Cree par un pro du batiment</h3>
              <p className="text-white/80 leading-relaxed">
                BatiAssist n'est pas un logiciel cree par des ingenieurs dans un bureau. 
                C'est un outil forge par l'experience terrain, pour repondre aux vrais besoins des artisans.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-ba-primary">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pret a simplifier ta gestion ?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Découvre comment BatiAssist peut transformer ton quotidien d'artisan.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-bold rounded-full px-10 py-7 text-lg shadow-xl hover:shadow-2xl transition-all">
                Decouvrir le CRM BatiAssist
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
